"use client"

import { useState, useEffect } from "react"
import { Link, useNavigate, useLocation } from "react-router-dom"
import { useSelector, useDispatch } from "react-redux"
import { Modal } from "react-bootstrap"
import parse from "html-react-parser"
import { User, Mail, Lock, Eye, EyeOff, Globe, Phone, KeyRound, ArrowLeft, Leaf, X, Info } from "lucide-react"
import { notify } from "../../../utility/Helper1"
import { userSelect, allRemoteSettingsSelect } from "../../../redux/selectors/index"
import { authActions } from "../../../redux/actions"
import { staticCountries } from "../../../utility/countries"
import ReCAPTCHA from "../../../utility/customComponents/ReCAPTCHA"
import AuthHolder from "./AuthHolder";

// Get referral from localStorage
const referralToUse = localStorage?.getItem("ref") != "null" ? localStorage?.getItem("ref") : ""

const SignupPage = () => {
  const dispatch = useDispatch()
  const users = useSelector(userSelect)
  const site_settings = useSelector(allRemoteSettingsSelect)

  const [username, setUsername] = useState("")
  const [email, setEmail] = useState("")
  const [withdrawalPin, setWithdrawalPin] = useState("")
  const [showPin, setShowPin] = useState(false)
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [retypePassword, setRetypePassword] = useState("");

  const [country, setCountry] = useState(0)
  const [phone, setPhone] = useState("")
  const [phoneCode, setPhoneCode] = useState('...')
  const [referral, setReferrals] = useState(referralToUse)
  const [agree, setAgree] = useState(false)
  const [walletAddress, setWalletAddress] = useState("")
  const [bank, setBankName] = useState("")
  const [accountName, setAccountName] = useState("")
  const [accountNumber, setAccountNumber] = useState("")
  const [depositMethod, setDepositMethod] = useState(100)

  const [loading, changeLoading] = useState(false)
  const [loadingText, setLoadingText] = useState("")
  const [fullPageLoading, setFullPageLoading] = useState(false)
  const [captcha, setCaptcha] = useState(null)
  const [show, setShow] = useState(false)

  const navigate = useNavigate()
  const location = useLocation()
  const search = location.search
  const referalLink = new URLSearchParams(search).get("ref")

  // Handle captcha change
  const captchaHasChanged = (data) => {
    setCaptcha(data)
  }

  // Modal handlers
  const handleClose = () => setShow(false)
  const handleShow = () => setShow(true)

  // Check for referral in URL
  useEffect(() => {
    if (referalLink != "" && referalLink != null && referalLink != "null") {
      localStorage.setItem("ref", referalLink)
      setReferrals(referalLink)
    }
  }, [referalLink])

  // Toggle agreement checkbox
  const checkboxHandler = () => {
    setAgree(!agree)
  }

  // Handle country change
  const changeCountry = (countryId) => {
    const selectedCountry = staticCountries.filter((e) => e.id == countryId)
    setCountry(countryId)
    const countryPhoneCode = selectedCountry?.[0]?.phonecode
    setPhoneCode(countryPhoneCode)
  }

  // Handle registration
  const doRegister = async (e) => {
    e.preventDefault()

    // Add this check at the beginning of doRegister function
    if (password !== retypePassword) {
      notify("Passwords don't match", 0);
      return;
    }

    // Check terms agreement if required
    if (!agree && (site_settings?.site_settings?.term ?? "") != "") {
      notify("You must accept the terms and conditions to continue", 0)
      return
    }

    // Check captcha if required
    if (!captcha && Number(site_settings?.site_settings?.use_google_reCaptcha) == 1) {
      notify("Invalid captcha response", 0)
      return
    }

    setFullPageLoading(true)
    changeLoading(true)
    setLoadingText("Please wait...")

    // Prepare payload
    const payLoad = {
      captcha,
      depositMethod,
      username,
      email,
      password,
      country,
      phone,
      withdrawalPin,
      referral,
    }

    // Add financial details based on deposit method
    let financeLoad
    if (depositMethod == 0) {
      financeLoad = { bank, accountName, accountNumber }
    } else {
      financeLoad = { walletAddress }
    }

    // Dispatch signup action
    dispatch(
      authActions.onSignUp("account/register", Object.assign({}, payLoad, financeLoad), (response) => {
        if (response.code != 0) {
          localStorage.removeItem("ref")
          navigate("/verify-user", { replace: true })
        } else {
          notify(response.msg, response.code)
        }

        changeLoading(false)
        setLoadingText("")
        setFullPageLoading(false)
      }),
    )
  }

  // Check if registration is open or invitation-only
  const openRegBasedOnRefSett =
    ((site_settings?.operational_settings?.useCompulsoryReferral ?? 0) == 1 && referral != "" && referral != null) ||
    (site_settings?.operational_settings?.useCompulsoryReferral ?? 0) == 0

  return (
    <AuthHolder>
      {openRegBasedOnRefSett ? (
        <>
          <div className="space-y-1 p-6 pb-4 relative z-10">
            <h2 className="text-2xl font-bold text-center text-blue-400">Create Account</h2>
            <p className="text-center text-slate-400">Sign up to get started</p>
          </div>

          <div className="p-6 pt-2 relative z-10">
            <form onSubmit={doRegister} className="space-y-5">
              <div className="space-y-2">
                <label htmlFor="username" className="block text-slate-300 text-sm font-medium">
                  Username
                </label>
                <div className="relative group">
                  <User className="absolute left-3 top-2.5 h-5 w-5 text-slate-400 group-focus-within:text-blue-400 transition-colors" />
                  <input
                    id="username"
                    type="text"
                    placeholder="Enter username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    className="w-full pl-10 pr-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-slate-200 placeholder-slate-500 transition-all"
                  />
                  <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-blue-500 to-transparent opacity-0 group-focus-within:opacity-100 transition-opacity"></div>
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="email" className="block text-slate-300 text-sm font-medium">
                  Email Address
                </label>
                <div className="relative group">
                  <Mail className="absolute left-3 top-2.5 h-5 w-5 text-slate-400 group-focus-within:text-blue-400 transition-colors" />
                  <input
                    id="email"
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full pl-10 pr-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-slate-200 placeholder-slate-500 transition-all"
                  />
                  <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-blue-500 to-transparent opacity-0 group-focus-within:opacity-100 transition-opacity"></div>
                </div>
              </div>



              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <label htmlFor="password" className="block text-slate-300 text-sm font-medium">
                    Password
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-xs text-blue-400 hover:text-blue-300 hover:underline transition-colors"
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
                <div className="relative group">
                  <Lock className="absolute left-3 top-2.5 h-5 w-5 text-slate-400 group-focus-within:text-blue-400 transition-colors" />
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="w-full pl-10 pr-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-slate-200 placeholder-slate-500 transition-all"
                  />
                  <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-blue-500 to-transparent opacity-0 group-focus-within:opacity-100 transition-opacity"></div>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <label htmlFor="retypePassword" className="block text-slate-300 text-sm font-medium">
                    Retype Password
                  </label>
                </div>
                <div className="relative group">
                  <Lock className="absolute left-3 top-2.5 h-5 w-5 text-slate-400 group-focus-within:text-blue-400 transition-colors" />
                  <input
                    id="retypePassword"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={retypePassword}
                    onChange={(e) => setRetypePassword(e.target.value)}
                    required
                    className="w-full pl-10 pr-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-slate-200 placeholder-slate-500 transition-all"
                  />
                  <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-blue-500 to-transparent opacity-0 group-focus-within:opacity-100 transition-opacity"></div>
                </div>
              </div>


              {/* <div className="space-y-2">
                <label htmlFor="password" className="block text-slate-300 text-sm font-medium">
                  Password
                </label>
                <div className="relative group">
                  <Lock className="absolute left-3 top-2.5 h-5 w-5 text-slate-400 group-focus-within:text-blue-400 transition-colors" />
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="w-full pl-10 pr-10 py-2 bg-slate-900/50 border border-slate-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-slate-200 placeholder-slate-500 transition-all"
                  />
                  <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-blue-500 to-transparent opacity-0 group-focus-within:opacity-100 transition-opacity"></div>
                </div>
              </div> */}

              <div className="space-y-2">
                <label htmlFor="country" className="block text-slate-300 text-sm font-medium">
                  Country
                </label>
                <div className="relative group">
                  <Globe className="absolute left-3 top-2.5 h-5 w-5 text-slate-400 group-focus-within:text-blue-400 transition-colors" />
                  <select
                    id="country"
                    value={country}
                    onChange={(e) => changeCountry(e.target.value)}
                    required
                    className="w-full pl-10 pr-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-slate-200 placeholder-slate-500 transition-all appearance-none"
                  >
                    <option hidden value="">
                      Select your country
                    </option>
                    {staticCountries.map((item, index) => (
                      <option key={index} value={item.id}>
                        {item.nicename}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                    <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </div>
                  <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-blue-500 to-transparent opacity-0 group-focus-within:opacity-100 transition-opacity"></div>
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="email" className="block text-slate-300 text-sm font-medium">
                  Phone
                </label>
                <div className="relative group flex items-center max-w-md">
                  {/* <Phone className="absolute left-3 top-2.5 h-5 w-5 text-slate-400 group-focus-within:text-blue-400 transition-colors" /> */}
                  <span className="px-3 py-2 bg-gray-100 text-gray-600 border border-gray-300 rounded-l-md">
                    {phoneCode}
                  </span>
                  <input
                    id="phone"
                    type="tel"
                    placeholder="Phone Number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    required
                    className="w-full pl-2 pr-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-slate-200 placeholder-slate-500 transition-all"
                  />
                  <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-blue-500 to-transparent opacity-0 group-focus-within:opacity-100 transition-opacity"></div>
                </div>
              </div>



              {(site_settings?.operational_settings?.request_pin_on_reg ?? 0) == 1 && (
                <div className="space-y-2">
                  <label htmlFor="security-pin" className="block text-slate-300 text-sm font-medium">
                    Security PIN
                  </label>
                  <div className="relative group">
                    <KeyRound className="absolute left-3 top-2.5 h-5 w-5 text-slate-400 group-focus-within:text-blue-400 transition-colors" />
                    <input
                      id="security-pin"
                      type={showPin ? "text" : "password"}
                      placeholder="••••"
                      value={withdrawalPin}
                      onChange={(e) => setWithdrawalPin(e.target.value)}
                      maxLength={site_settings?.operational_settings?.security_pin_length}
                      required
                      className="w-full pl-10 pr-10 py-2 bg-slate-900/50 border border-slate-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-slate-200 placeholder-slate-500 transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPin(!showPin)}
                      className="absolute right-3 top-2.5 text-slate-400 hover:text-blue-400 transition-colors"
                    >
                      {showPin ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      <span className="sr-only">{showPin ? "Hide PIN" : "Show PIN"}</span>
                    </button>
                    <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-blue-500 to-transparent opacity-0 group-focus-within:opacity-100 transition-opacity"></div>
                  </div>
                </div>
              )}

              <input type="hidden" value={referral} onChange={(e) => setReferrals(e.target.value)} />

              <div className="pt-1">
                <ReCAPTCHA onChange={captchaHasChanged} />
              </div>

              {(site_settings?.site_settings?.term ?? "") != "" && (
                <div className="flex items-start mt-4">
                  <div className="flex items-center h-5">
                    <input
                      id="agree"
                      type="checkbox"
                      checked={agree}
                      onChange={checkboxHandler}
                      className="h-4 w-4 rounded border-slate-700 bg-slate-800 text-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="agree" className="text-slate-400">
                      I agree to the{" "}
                      <button
                        type="button"
                        onClick={handleShow}
                        className="text-blue-400 hover:text-blue-300 hover:underline font-medium"
                      >
                        Terms of Service
                      </button>
                    </label>
                  </div>
                </div>
              )}

              <button
                type="submit"
                className="w-full bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white font-medium py-2.5 px-4 rounded-lg transition-all duration-200 shadow-md hover:shadow-blue-500/20 disabled:opacity-70 disabled:cursor-not-allowed mt-2 relative overflow-hidden group"
                disabled={loading}
              >
                <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-blue-400 to-indigo-500 opacity-0 group-hover:opacity-40 transition-opacity duration-300 transform -translate-x-full group-hover:translate-x-0"></span>
                {loading ? (
                  <div className="flex items-center justify-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Creating Account...
                  </div>
                ) : (
                  <span className="relative z-10">Create Account</span>
                )}
              </button>
            </form>
          </div>

          <div className="px-6 pb-6 pt-0 relative z-10">
            <div className="relative w-full my-4">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-slate-700" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-slate-800 px-2 text-slate-400">or</span>
              </div>
            </div>

            <p className="text-center text-sm text-slate-400">
              <Link
                to="/signin"
                className="font-medium text-blue-400 hover:text-blue-300 transition-colors"
              >
                Sign in
              </Link>
            </p>
          </div>
        </>
      ) : (
        <div className="p-8 text-center relative z-10">
          <div className="flex justify-center mb-4">
            <div className="bg-blue-900/30 p-3 rounded-full">
              <Info className="h-8 w-8 text-blue-400" />
            </div>
          </div>
          <h3 className="text-xl font-semibold text-blue-400 mb-2">Registration by Invitation Only</h3>
          <p className="text-slate-400 mb-6">
            We're currently accepting new users by invitation only. Please contact an existing member for a
            referral.
          </p>
          <Link
            to="/signin"
            className="inline-block bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white font-medium py-2 px-4 rounded-lg transition-all duration-200 shadow-md hover:shadow-blue-500/20"
          >
            Return to Login
          </Link>
        </div>
      )}

      {/* Terms and Conditions Modal */}
      <Modal show={show} onHide={handleClose} centered>
        <div className="bg-slate-800 rounded-lg shadow-xl overflow-hidden border border-slate-700">
          <div className="flex items-center justify-between p-4 border-b border-slate-700">
            <h3 className="text-lg font-semibold text-blue-400">Terms and Conditions</h3>
            <button
              onClick={handleClose}
              className="text-slate-400 hover:text-slate-300 focus:outline-none"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="p-6 max-h-[60vh] overflow-y-auto">
            <div className="prose prose-invert max-w-none text-slate-300">
              {parse(site_settings?.site_settings?.term ?? "")}
            </div>
          </div>

          <div className="flex justify-end p-4 border-t border-slate-700">
            <button
              onClick={handleClose}
              className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white font-medium py-2 px-4 rounded-lg transition-all duration-200"
            >
              I Understand
            </button>
          </div>
        </div>
      </Modal>
    </AuthHolder>
  )
}

export default SignupPage